import React, { useEffect, useRef, useState } from "react";
import { Button, Select, Tag, message } from "antd";
import { getSuperAdminLogs } from "../../apis/superadmin";
import { format } from "date-fns";

type LogEntry = {
  timestamp?: string | null;
  level?: string;
  message?: string;
  raw: string;
};

const LEVEL_COLORS: Record<string, string> = {
  EMERGENCY: "magenta",
  ALERT: "magenta",
  CRITICAL: "red",
  ERROR: "red",
  WARNING: "orange",
  NOTICE: "blue",
  INFO: "cyan",
  DEBUG: "default",
  UNKNOWN: "default",
};

const LINE_OPTIONS = [
  { value: 200, label: "Last 200 lines" },
  { value: 500, label: "Last 500 lines" },
  { value: 1000, label: "Last 1000 lines" },
  { value: 2000, label: "Last 2000 lines" },
];

const LEVEL_OPTIONS = [
  { value: "", label: "All levels" },
  { value: "ERROR", label: "Error" },
  { value: "WARNING", label: "Warning" },
  { value: "INFO", label: "Info" },
  { value: "DEBUG", label: "Debug" },
  { value: "CRITICAL", label: "Critical" },
];

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Logs = () => {
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState(500);
  const [level, setLevel] = useState("");
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [meta, setMeta] = useState<{
    exists: boolean;
    path?: string;
    size?: number;
    updatedAt?: string | null;
    message?: string;
  }>({ exists: true });
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchLogs = () => {
    setLoading(true);
    getSuperAdminLogs({ lines, level: level || undefined })
      .then((res: any) => {
        const data = res.data;
        setMeta({
          exists: data.exists,
          path: data.path,
          size: data.size,
          updatedAt: data.updatedAt ?? data.updated_at,
          message: data.message,
        });
        setEntries(data.entries ?? []);
      })
      .catch(() => message.error("Failed to load logs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLogs();
  }, [lines, level]);

  useEffect(() => {
    if (!loading) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [entries, loading]);

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Error Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Recent entries from Laravel{" "}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">storage/logs/laravel.log</code>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={lines}
            onChange={setLines}
            options={LINE_OPTIONS}
            style={{ width: 160 }}
          />
          <Select
            value={level}
            onChange={setLevel}
            options={LEVEL_OPTIONS}
            style={{ width: 140 }}
          />
          <Button onClick={fetchLogs} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
        {meta.exists ? (
          <>
            <span className="bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              Size: <strong className="text-gray-900">{formatBytes(meta.size ?? 0)}</strong>
            </span>
            <span className="bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              Entries: <strong className="text-gray-900">{entries.length}</strong>
            </span>
            {meta.updatedAt && (
              <span className="bg-white border border-gray-200 rounded-lg px-3 py-1.5">
                Updated:{" "}
                <strong className="text-gray-900">
                  {format(new Date(meta.updatedAt), "dd MMM yyyy, HH:mm:ss")}
                </strong>
              </span>
            )}
          </>
        ) : (
          <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            {meta.message || "Log file not found."}
          </span>
        )}
      </div>

      <div className="bg-[#0f172a] rounded-xl shadow-sm border border-slate-800 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-700 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
            laravel.log
          </span>
          <span className="text-xs text-slate-500">
            {loading ? "Loading…" : "Newest at bottom"}
          </span>
        </div>

        <div className="max-h-[70vh] overflow-auto p-4 font-mono text-[12px] leading-relaxed">
          {!loading && entries.length === 0 ? (
            <div className="text-slate-500 py-8 text-center">No log entries to show.</div>
          ) : (
            entries.map((entry, idx) => {
              const levelKey = (entry.level || "UNKNOWN").toUpperCase();
              return (
                <div
                  key={`${entry.timestamp ?? "row"}-${idx}`}
                  className="mb-4 last:mb-0 border-b border-slate-800/80 pb-4 last:border-0"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {entry.timestamp && (
                      <span className="text-slate-400">{entry.timestamp}</span>
                    )}
                    <Tag color={LEVEL_COLORS[levelKey] ?? "default"} className="m-0">
                      {levelKey}
                    </Tag>
                  </div>
                  <pre className="whitespace-pre-wrap break-words text-slate-200 m-0">
                    {entry.message || entry.raw}
                  </pre>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
};

export default Logs;
